import { Controller } from "react-hook-form"
import { CreateEntityDialog } from "src/shared/components"
import OutlinedInput from "src/shared/components/outlined-input"
import Switch from "src/shared/components/switch"
import { Stack } from "src/shared/components"
import * as yup from "yup"
import { ShipmentStatus } from "src/types/modules"
import { toast } from "react-toastify"
import {
  useCreateUtilityMutation,
  useUpdateUtilityMutation,
  ModuleEnum,
  Utility,
  CreateUtilityVariable,
  UpdateUtilityVariable
} from "src/api/modules/utilitize"

type ShipmentStatusFormData = Omit<ShipmentStatus, "id" | "createdAt" | "createdBy">

const shipmentStatusSchema = yup.object().shape({
  status: yup.string().required("Status is required"),
  active: yup.boolean().required("Active is required"),
  additionalInformation: yup.string().optional()
})

interface CreateShipmentStatusDialogProps {
  open: boolean
  onClose: () => void
  utility?: Partial<Utility>
}

const CreateShipmentStatusDialog = ({ open, onClose, utility }: CreateShipmentStatusDialogProps) => {
  const [createStatus, { isLoading }] = useCreateUtilityMutation()
  const [updateStatus, { isLoading: isUpdateLoading }] = useUpdateUtilityMutation()

  const handleSave = async (data: ShipmentStatusFormData) => {
    let resp: any
    const utilityData: CreateUtilityVariable = {
      itemName: data.status,
      active: data.active,
      additionalInformation: data.additionalInformation || "",
      module: ModuleEnum.SHIPMENT_STATUS
    }

    if (utility?.id) {
      const updateData: UpdateUtilityVariable = {
        ...utilityData,
        id: utility.id
      }
      resp = await updateStatus(updateData)
    } else {
      resp = await createStatus(utilityData)
    }
    if (resp?.data?.data?.id) {
      toast.success(utility?.id ? "Shipment status updated successfully" : "Shipment status created successfully")
      onClose()
    }
  }

  return (
    <CreateEntityDialog
      open={open}
      onClose={onClose}
      isLoading={isLoading || isUpdateLoading}
      title={utility?.id ? "Edit Shipment Status" : "Create New Shipment Status"}
      schema={shipmentStatusSchema}
      defaultValues={{
        status: utility?.itemName || "",
        active: utility?.active ?? true,
        additionalInformation: utility?.additionalInformation || ""
      }}
      onSave={handleSave}
    >
      {(form) => (
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Controller
              control={form.control}
              name="status"
              render={({ field, fieldState }) => (
                <OutlinedInput
                  {...field}
                  fullWidth
                  label="Status"
                  placeholder="Enter shipment status"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              control={form.control}
              name="active"
              render={({ field }) => (
                <Switch title="Active" subtitle="Active/Inactive" onChange={field.onChange} value={field.value} />
              )}
            />
          </Stack>
          <Controller
            control={form.control}
            name="additionalInformation"
            render={({ field, fieldState }) => (
              <OutlinedInput
                {...field}
                multiline
                rows={3}
                label="Additional Information"
                placeholder="Enter additional information"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Stack>
      )}
    </CreateEntityDialog>
  )
}

export default CreateShipmentStatusDialog
